import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { FetchOptionsCallback } from 'global';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';

import { calendarApi, ReDateEventsInput } from './index';

export function useCalendar() {
  const { showNotification } = useNotifications();

  const [onComplete, { loading: isCompleting }] = useMutation(calendarApi.events.mutations.complete);

  const handleCompleteEvent = useCallback(
    async (ids: string[], fetchOptions?: FetchOptionsCallback<typeof calendarApi>) => {
      try {
        const { data } = await onComplete({
          variables: { input: { ids } },
          ...(typeof fetchOptions === 'function' ? fetchOptions(calendarApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification(`Successfully completed ${ids.length === 1 ? 'event' : 'events'}`);

        return data.dealActivity.result.list;
      } catch (error) {
        logError(error);
        showNotification(`Something went wrong completing ${ids.length === 1 ? 'event' : 'events'}`, { type: 'error' });
      }
    },
    [onComplete, showNotification]
  );

  const [onReDate, { loading: isReDating }] = useMutation(calendarApi.events.mutations.reDate);

  const handleReDateEvent = useCallback(
    async (input: ReDateEventsInput['input'], fetchOptions?: FetchOptionsCallback<typeof calendarApi>) => {
      try {
        const { data } = await onReDate({
          variables: { input },
          ...(typeof fetchOptions === 'function' ? fetchOptions(calendarApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification(`Successfully re-dated ${input.events.length === 1 ? 'event' : 'events'}`);

        return data.dealActivity.result.list;
      } catch (error) {
        logError(error);
        showNotification(`Something went wrong re-dating calendar ${input.events.length === 1 ? 'event' : 'events'}`, {
          type: 'error',
        });
      }
    },
    [onReDate, showNotification]
  );

  const [onMoveToTomorrow, { loading: isMovingToTomorrow }] = useMutation(calendarApi.events.mutations.moveToTomorrow);

  const handleMoveToTomorrowEvent = useCallback(
    async (ids: string[], fetchOptions?: FetchOptionsCallback<typeof calendarApi>) => {
      try {
        const { data } = await onMoveToTomorrow({
          variables: { input: { ids } },
          ...(typeof fetchOptions === 'function' ? fetchOptions(calendarApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification(`Successfully moved ${ids.length === 1 ? 'event' : 'events'} to tomorrow`);

        return data.dealActivity.result.list;
      } catch (error) {
        logError(error);
        showNotification(`Something went wrong moving ${ids.length === 1 ? 'event' : 'events'} to tomorrow`, {
          type: 'error',
        });
      }
    },
    [onMoveToTomorrow, showNotification]
  );

  const [onAddEventNote, { loading: isAddingEventNote }] = useMutation(calendarApi.events.mutations.addEventNote);

  const handleAddEventNote = useCallback(
    async (id: string, note: string, fetchOptions?: FetchOptionsCallback<typeof calendarApi>) => {
      try {
        const { data } = await onAddEventNote({
          variables: { input: { eventActivityId: id, content: note } },
          ...(typeof fetchOptions === 'function' ? fetchOptions(calendarApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification(`Successfully added note to ${id.length === 1 ? 'event' : 'events'}`);

        return data.dealActivity.result.entity;
      } catch (error) {
        logError(error);
        showNotification(`Something went wrong adding note to ${id.length === 1 ? 'event' : 'events'}`, {
          type: 'error',
        });
      }
    },
    [onAddEventNote, showNotification]
  );

  const [onAddCalendarView, { loading: isAddingCalendarView }] = useMutation(calendarApi.sharing.mutations.addView);

  const handleAddCalendarView = useCallback(
    async (
      dto: {
        color: string;
        name: string;
        userId: string;
      },
      fetchOptions?: FetchOptionsCallback<typeof calendarApi>
    ) => {
      try {
        const { data } = await onAddCalendarView({
          variables: { input: dto },
          ...(typeof fetchOptions === 'function' ? fetchOptions(calendarApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification(`Successfully added!`);

        return data.dealActivity.result.entity;
      } catch (error) {
        logError(error);
        showNotification(`Something went wrong`, {
          type: 'error',
        });
      }
    },
    [onAddCalendarView, showNotification]
  );

  const [onUpdateCalendarView, { loading: isUpdatingCalendarView }] = useMutation(
    calendarApi.sharing.mutations.updateView
  );

  const handleUpdateCalendarView = useCallback(
    async (
      dto: { id: string; name?: string; color?: string },
      fetchOptions?: FetchOptionsCallback<typeof calendarApi>
    ) => {
      try {
        const { data } = await onUpdateCalendarView({
          variables: { input: dto },
          ...(typeof fetchOptions === 'function' ? fetchOptions(calendarApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification(`Successfully updated!`);

        return data.dealActivity.result.entity;
      } catch (error) {
        logError(error);
        showNotification(`Something went wrong`, {
          type: 'error',
        });
      }
    },
    [onUpdateCalendarView, showNotification]
  );

  const [onDeleteCalendarView, { loading: isDeletingCalendarView }] = useMutation(
    calendarApi.sharing.mutations.deleteView
  );

  const handleDeleteCalendarView = useCallback(
    async (id: string, fetchOptions?: FetchOptionsCallback<typeof calendarApi>) => {
      try {
        const { data } = await onDeleteCalendarView({
          variables: { input: { id } },
          ...(typeof fetchOptions === 'function' ? fetchOptions(calendarApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification(`Successfully deleted`);

        return data.dealActivity.result.entity;
      } catch (error) {
        logError(error);
        showNotification(`Something went wrong`, {
          type: 'error',
        });
      }
    },
    [onDeleteCalendarView, showNotification]
  );

  const [onToggleView, { loading: isToggingView }] = useMutation(calendarApi.sharing.mutations.toggleView);

  const handleToggleView = useCallback(
    async (dto: { id: string; status: boolean }, fetchOptions?: FetchOptionsCallback<typeof calendarApi>) => {
      try {
        const { data } = await onToggleView({
          variables: { input: dto },
          ...(typeof fetchOptions === 'function' ? fetchOptions(calendarApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification(`Successfully deleted`);

        return data.dealActivity.result.entity;
      } catch (error) {
        logError(error);
        showNotification(`Something went wrong`, {
          type: 'error',
        });
      }
    },
    [onToggleView, showNotification]
  );

  return {
    handleToggleView,
    handleReDateEvent,
    handleAddEventNote,
    handleCompleteEvent,
    handleMoveToTomorrowEvent,
    handleAddCalendarView,
    handleUpdateCalendarView,
    handleDeleteCalendarView,
    isReDating,
    isCompleting,
    isToggingView,
    isAddingEventNote,
    isMovingToTomorrow,
    isAddingCalendarView,
    isUpdatingCalendarView,
    isDeletingCalendarView,
  };
}
